import * as _ from "lodash-es";
import { ParseResult } from "papaparse";
import React, { ChangeEvent, useState, useEffect } from "react";
import ICreateVendorUserCsvDTO from "../../../../models/DTO/ICreateVendorUserCsvDTO";
import OwCsvReader from "../../../shared/OwCsvReader/OwCsvReader";
import OwButton from "../../../shared/OwButton/OwButton";
import IErrorDTO from "../../../../models/DTO/IErrorDTO";
import OwCheckbox from "../../../shared/OwCheckbox/OwCheckbox";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import ISupplierModel from "../../../../models/ISupplierModel";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import ICatalogItemTemplateRow from "../../../../models/ICatalogItemTemplateRow";
import IOnCreateVendorUserCsvDTO from "../../../../models/DTO/IOnCreateVendorUserCsvDTO";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import IUserUI from "../../../../models/IUserUI";
import { Socket } from "socket.io-client";

type Props = {
    userModel: IUserUI;
    supplierModel: ISupplierModel;
    closeModalFn: () => void;
    socket: Socket;
    notifyFn: (data: IErrorDTO | null, _notification?: any, isFormatted?: boolean) => void;
};

const VendorUsersUploadModal: React.FC<Props> = ({ userModel, supplierModel, closeModalFn, socket, notifyFn }) => {
    const [isProcessing, setIsProcessing] = useState(false);
    const [csvData, setCsvData] = useState<ParseResult<any> | undefined>();
    const [showResults, setShowResults] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [processedRows, setProcessedRows] = useState(0);
    const [uploadActive, setUploadActive] = useState(false);
    const [create_sso_user, setCreateSsoUser] = useState(supplierModel.has_sso);
    const [loadedDataRows, setLoadedDataRows] = useState<ICatalogItemTemplateRow[] | undefined>();
    const [totalRowsLoaded, setTotalRowsLoaded] = useState(0);

    useEffect(() => {
        const onCreateVendorUserCsv = (data: IOnCreateVendorUserCsvDTO) => {
            setTotalRecords(data.totalRows);
            setProcessedRows(data.processedRows);
            setShowResults(true);
            setUploadActive(false);
        };

        socket.on(EndpointsEnum.VendorsOnCreateVendorUserCsv, onCreateVendorUserCsv);

        return () => {
            socket.removeListener(EndpointsEnum.VendorsOnCreateVendorUserCsv, onCreateVendorUserCsv);
        };
    }, [socket]);

    const setupVendorUsersData = (csvData: ParseResult<any>) => {
        const vendorName = _.startCase(supplierModel.vendor_name_override || "vendor");

        const vendorCol = vendorName;
        const nameCol = "Name";
        const emailCol = "Email";
        const locationCol = "Location";

        const items: ICatalogItemTemplateRow[] = [];

        for (const row of csvData.data) {
            const dataRow: any = {
                vendor: row[vendorCol],
                name: row[nameCol],
                email: row[emailCol],
            };

            if (row[locationCol]) {
                dataRow.location = row[locationCol];
            }

            if (dataRow.vendor && dataRow.name && dataRow.email) {
                items.push(dataRow);
            }
        }

        setLoadedDataRows(items);
        setTotalRowsLoaded(csvData.data.length);
    };

    const createVendorUserCSV = () => {
        if (loadedDataRows) {
            const payload: ICreateVendorUserCsvDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                rows: JSON.stringify(loadedDataRows),
                create_sso_user: create_sso_user,
            };

            socket.emit(EndpointsEnum.VendorsCreateVendorUserCsv, payload);

            setShowResults(false);
            setUploadActive(true);
            setProcessedRows(0);
            setTotalRecords(0);
        }
    };

    const doUpload = () => {
        if (csvData) {
            setupVendorUsersData(csvData);
        }
    };

    const csvLoaded = (csvData?: ParseResult<any>) => {
        if (csvData) {
            setCsvData(csvData);
        }
    };

    const closeModal = () => {
        closeModalFn();
    };

    const handleChange = (e: ChangeEvent<any>) => {
        const name = e.target.name;
        const value = e.target.value;

        if (name === "create_sso_user") {
            setCreateSsoUser(value);
        }
    };

    return (
        <div className="VendorUsersUploadModal p-2">
            <div className={"row"}>
                <div className={"col d-flex justify-content-between"}>
                    <OwTitle leftIconName={"fad fa-cloud-upload-alt"} title={"upload_csv"} tag={"h3"} />
                    <div>
                        <OwButton className={"btn-danger"} centerIconClass={"fas fa-times"} fn={closeModal} />
                    </div>
                </div>
            </div>

            <div className={"row"}>
                <div className={"col-12"}>
                    <div className={"drop-area"}>
                        <OwCsvReader onFileLoaded={csvLoaded} isProcessing={isProcessing} />
                    </div>
                </div>
            </div>

            {supplierModel.has_sso ? (
                <div>
                    <OwCheckbox
                        label={"create_sso_user"}
                        name={"create_sso_user"}
                        checked={create_sso_user}
                        truthyVal={true}
                        falsyVal={false}
                        fn={handleChange}
                    />
                </div>
            ) : null}

            {loadedDataRows ? (
                <div>
                    <OwLabel title={"loaded_rows"} postText={":"} />
                    {loadedDataRows.length}
                </div>
            ) : null}

            {showResults ? null : (
                <div className={"row"}>
                    <div className={"col-12 mt-4"}>
                        <OwButton title={"process"} fn={doUpload} className={"btn-primary me-2"} />
                        <OwButton
                            title={"upload"}
                            fn={createVendorUserCSV}
                            active={uploadActive}
                            disableOnActive={true}
                            disabled={!loadedDataRows?.length}
                        />
                    </div>
                </div>
            )}

            {showResults ? (
                <div className={"mt-2"}>
                    <div>
                        <OwLabel title={"total_records"} noBottomMargin={true} className={"me-3"} isBold={true} />
                        {totalRecords}
                    </div>

                    <div>
                        <OwLabel title={"records_updated"} noBottomMargin={true} className={"me-3"} isBold={true} />
                        {processedRows}
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default VendorUsersUploadModal;
